// The list of all possible keys used in
// both editable fields and URL parameters
export const keys = Object.freeze({
  APR: 'apr',
  CACHED_RATES_PURCHASE: 'cachedRatesPurchase',
  CACHED_RATES_REFINANCE: 'cachedRatesRefinance',
  CACHED_REQUEST_STATE: 'cachedRequestState',
  CASH_OUT_AMOUNT: 'cashOutAmount',
  CURRENT_LOCATION: 'currentLocation',
  CITY: 'city',
  CLOSING_COSTS: 'closingCosts',
  CREDIT_SCORE: 'creditScore',
  FILTER_STATE: 'filterState',
  HAS_VISITED: 'hasVisited',
  HOME_VALUE: 'homeValue',
  LOAN_AMOUNT: 'loanAmount',
  LOAN_SIZE: 'loanSize',
  LOAN_STATE: 'loanState',
  LOAN_PURPOSE: 'loanPurpose',
  OCCUPANCY_TYPE: 'occupancyType',
  PAYMENT: 'payment',
  PROPERTY_TYPE: 'propertyType',
  RATE: 'rate',
  SORT_STATE: 'sortState',
  STATE: 'state',
  USER_SET_LOCATION: 'userSetLocation',
  ZIP_CODE: 'zipCode',
  ZIP_CODES: 'zipCodes',
  FOOTPRINT: 'footprint'
});

export const loanPurpose = Object.freeze({
  REFINANCE: 'refinance',
  PURCHASE: 'purchase'
});

export const closingCostType = Object.freeze({
  NCC: 'ncc',
  POINTS: 'ptsfees',
  BOTH: 'both'
});

export const productLoadState = Object.freeze({
  NOT_STARTED: 'not_started',
  QUEUED: 'queued',
  LOADING: 'loading',
  PRELOADING: 'preloading',
  PARTIALLY_LOADED: 'partially_loaded',
  QUEUED_LOAD_MORE: 'queued_loading_more',
  LOADING_MORE: 'loading_more',
  COMPLETE: 'complete',
  ERROR: 'error'
});

export const errorSource = Object.freeze({
  SERVER: 'server',
  CLIENT: 'client'
});

// The list of all possible fields to include in
// the loan assumptions box for the user to edit.
export const field = Object.freeze({
  CREDIT_SCORE: keys.CREDIT_SCORE,
  HOME_VALUE: keys.HOME_VALUE,
  LOAN_TYPE: keys.LOAN_TYPE,
  LOAN_AMOUNT: keys.LOAN_AMOUNT,
  OCCUPANCY_TYPE: keys.OCCUPANCY_TYPE,
  PROPERTY_TYPE: keys.PROPERTY_TYPE,
  ZIP_CODE: keys.ZIP_CODE,
  CASH_OUT_AMOUNT: keys.CASH_OUT_AMOUNT
});

export const fieldTypes = Object.freeze({
  TEXT: 'text',
  SELECT: 'select'
});

export const requestBody = Object.freeze({
  LoanAmount: keys.LOAN_AMOUNT,
  PropertyValue: keys.HOME_VALUE,
  LoanPurpose: keys.LOAN_TYPE,
  PropertyType: keys.PROPERTY_TYPE,
  OccupancyType: keys.OCCUPANCY_TYPE,
  ZipCode: keys.ZIP_CODE,
  FicoScore: keys.CREDIT_SCORE,
  CashOutAmount: keys.CASH_OUT_AMOUNT
});

export const creditScoreRanges = Object.freeze({
  EXCELLENT: {
    value: '780',
    label: 'Excellent',
    info: '780+'
  },
  GREAT: {
    value: '779',
    label: 'Great',
    info: '760-779'
  },
  VERY_GOOD: {
    value: '759',
    label: 'Very good',
    info: '740-759'
  },
  ABOVE_AVERAGE: {
    value: '739',
    label: 'Above average',
    info: '720-739'
  },
  AVERAGE: {
    value: '719',
    label: 'Average',
    info: '700-719'
  },
  BELOW_AVERAGE: {
    value: '699',
    label: 'Below average',
    info: '680-699'
  },
  FAIR: {
    value: '679',
    label: 'Fair',
    info: '660-679'
  },
  LOW: {
    value: '659',
    label: 'Low',
    info: '640-659'
  },
  VERY_LOW: {
    value: '639',
    label: 'Very low',
    info: '620-639'
  },
  POOR: {
    value: '619',
    label: 'Poor',
    info: '619-'
  }
});

export const propertyTypes = Object.freeze({
  SFR: {
    value: 'Single Family - 1 unit',
    label: 'Single family home'
  },
  PUD: {
    value: 'PUD',
    label: 'Planned development'
  },
  CONDO: { value: 'Condo', label: 'Condo' },
  TWO_UNIT: {
    value: '2 Unit',
    label: '2 Unit',
    loanDetailsText: '2 unit property'
  },
  THREE_UNIT: {
    value: '3 Unit',
    label: '3 Unit',
    loanDetailsText: '3 unit property'
  },
  FOUR_UNIT: {
    value: '4 Unit',
    label: '4 Unit',
    loanDetailsText: '4 unit property'
  },
  MANUFACTURED: {
    value: 'Manufactured Home',
    label: 'Manufactured home'
  }
});

export const occupancyTypes = Object.freeze({
  OWNER_OCCUPIED: {
    value: 'Owner Occupied',
    label: 'Owner occupied'
  },
  SECOND_HOME: { value: 'Second Home', label: 'Second home' },
  INVESTMENT: { value: 'Investment Property', label: 'Investment' }
});

// The columns and order user can
// sort the rate table by
export const sortStates = Object.freeze({
  PRODUCT: {
    label: 'Product',
    value: 'termValue, rate, apr',
    order: 'desc, asc, asc'
  },
  RATE: {
    label: 'Rate',
    value: 'rate, apr, termValue',
    order: 'asc'
  },
  CLOSING_COSTS: {
    label: 'Points',
    value: 'closingCosts, termValue',
    order: 'asc'
  },
  PAYMENT: {
    label: 'Payment',
    value: 'payment',
    order: 'asc'
  }
});

// Rate table column keys (used for displaying badges). These must match
// the options in the BadgeDisplayField field of the BadgeInfo class
export const columnKeys = Object.freeze({
  PRODUCT: 'product',
  RATE: 'rate',
  POINTS: 'points',
  PAYMENT: 'payment'
});
